/**
 * `devices` procedure group — design §2.4 device pairing and management.
 *
 * The REDEMPTION half of pairing (`POST /devices/pair`) is deliberately NOT in
 * this contract: it is the one endpoint authenticated by a pairing code instead
 * of a credential, lives as a raw route in `apps/api`, and must never share the
 * authenticated middleware path. This group covers what a signed-in USER does:
 * mint a code, list their devices, revoke one.
 */

import { z } from 'zod';
import { isoDateTimeSchema, nonBlankString, uuidSchema } from './common.ts';

export const devicePlatformSchema = z.enum(['ios', 'macos', 'chrome_extension']);
export type DevicePlatform = z.infer<typeof devicePlatformSchema>;

export const deviceStatusSchema = z.enum(['active', 'revoked']);

export const deviceSchema = z.object({
  id: uuidSchema,
  platform: devicePlatformSchema,
  name: z.string().nullable(),
  status: deviceStatusSchema,
  /** Device-scoped capabilities, e.g. `recorder:write`. Set at pairing by platform. */
  capabilities: z.array(z.string()),
  lastSeenAt: isoDateTimeSchema.nullable(),
  createdAt: isoDateTimeSchema,
});
export type Device = z.infer<typeof deviceSchema>;

export const deviceCreatePairingCodeInputSchema = z.object({
  platform: devicePlatformSchema,
});

export const devicePairingCodeSchema = z.object({
  /**
   * The 8-char plaintext code, returned EXACTLY once — only its hash is stored.
   * Single use, 5-minute TTL.
   */
  code: nonBlankString(8, 'pairing code'),
  expiresAt: isoDateTimeSchema,
});

export const deviceListOutputSchema = z.object({
  items: z.array(deviceSchema),
});

export const deviceRevokeInputSchema = z.object({
  deviceId: uuidSchema,
});

/**
 * `devices.updatePushToken` — the iPhone's Expo push registration.
 *
 * ## Why it is a procedure of its own and not a field on a device PATCH
 *
 * There is no device PATCH, and there should not be one: everything else on a
 * `devices` row is set at pairing and is not the client's to edit. The push
 * token is the one exception, and it is not an edit so much as a REGISTRATION
 * that expires — Expo rotates a token when the app is reinstalled, restored to a
 * new phone, or when the user reinstalls after a long gap, and the client is
 * expected to send the current one on every launch.
 *
 * ## Why the caller cannot name a device
 *
 * `deviceId` is deliberately absent. The token belongs to the app instance
 * making the call, and that instance is already identified by its API key — so
 * the server writes the token onto the CALLER'S device row and nowhere else. A
 * `deviceId` parameter would let a compromised device redirect another device's
 * push notifications to itself, which is a notification-hijacking primitive in
 * exchange for no capability the client actually needs.
 *
 * `token: null` deregisters, which is what a sign-out should send: a stale token
 * is how a signed-out phone keeps buzzing with someone else's digest.
 */
export const devicePushTokenUpdateInputSchema = z.object({
  /**
   * An Expo push token: `ExponentPushToken[…]` or `ExpoPushToken[…]`. Validated
   * as a SHAPE here and again by `expo-server-sdk` at send time — a malformed
   * token stored now is a delivery that silently fails at 07:00 tomorrow.
   */
  token: z
    .string()
    .min(1)
    .max(256)
    .regex(/^Expo(nent)?PushToken\[[^\]]+\]$/, 'expected an Expo push token')
    .nullable(),
});

export const devicePushTokenSchema = z.object({
  ok: z.literal(true),
  deviceId: uuidSchema,
  /** True when a token is now registered; false after a deregistration. */
  registered: z.boolean(),
});
